import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import { useMounted } from '@vueuse/core';

import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useArbitrage } from '@/helpers/composables/useArbitrage';

import { usePromotion } from '@/helpers/composables/usePromotion';

import { OfferLocation } from '@/helpers/offer-helper';
import type { Offer } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { appleQualityTvCountries, qualityTVProviderConfig } from '@/components/buybox/QualityTvRow/data';
import BuyBoxFreeTrialSwiperOS from '@/components/buybox/BuyBoxFreeTrialSwiperOS.vue';
import { freeTrialSwiperRules } from '@/helpers/promotion-helper';
import { useGeoSwiperExp } from '@/components/experiments/GeoSwiperExp';
import { useSingleQTV } from '@/components/buybox-new/composables/useSingleQTV';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxPromotionRowNew',
  props: {
    offers: null,
    showFreeTrial: { type: Boolean },
    isFreeBuyBox: { type: Boolean },
    title: null,
    additionalContexts: null,
    trackingContexts: null,
    seasonNumber: null,
    episodeNumber: null,
    forceQualityTv: { type: Boolean }
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: TitleOfferFragment | null;
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offers: Offer[];
	showFreeTrial: boolean;
	isFreeBuyBox: boolean;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	trackingContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	// FEWER_PROMOTED_TAGS_EXP
	forceQualityTv?: boolean;
	// FEWER_PROMOTED_TAGS_EXP
};

const PromotionRowNew = () => import('@/components/buybox-new/PromotionRowNew.vue');
const BuyBoxQualityTVNew = () => import('@/components/buybox-new/qualityTV/BuyBoxQualityTVNew.vue');
const SingleQTV = () => import('@/components/buybox-new/qualityTV/SingleQTV.vue');



const { isProUser } = useUserStore();
const { isArbitrating } = useArbitrage();
const { country } = useLanguageStore();
const isMounted = useMounted();



const { promotionOffer, catalogueItem, inPromotionCatalogue } = usePromotion({
	title: () => props.title,
	offers: () => props.offers,
});

const showPromotionRowWhenArbitrating = computed(
	() => isArbitrating.value && 350 === promotionOffer.value?.package?.packageId
);

// GEO_SWIPER_EXP
const { geoSwiperExpVariant, geoSwiperLocation } = useGeoSwiperExp(() => props.title);

const geoSwiperActive = computed(() => {
	if (!isMounted.value || !geoSwiperExpVariant.value) return false;

	return ['variant_1', 'variant_2'].includes(geoSwiperExpVariant.value);
});
// GEO_SWIPER_EXP

const promotionPackageId = computed(() => {
	return promotionOffer.value?.package?.packageId;
});

const isQualityTvCountry = computed(() => {
	if (!promotionOffer.value) return false;

	if (promotionOffer.value.package.packageId === 2) return true;

	return (
		promotionOffer.value.package.packageId === 350 &&
		!inPromotionCatalogue.value &&
		appleQualityTvCountries.includes(country.value)
	);
});

const freeTrialActive = computed(() => {
	return (
		!isProUser.value &&
		(catalogueItem.value?.placement === 'ft' || (!catalogueItem.value?.placement && props.showFreeTrial))
	);
});

const hideTVODQTV = computed(
	() =>
		catalogueItem.value?.placement === 'qtv' &&
		!['FREE', 'JWTV'].includes(qualityTVProviderConfig[catalogueItem.value.packageId].type) &&
		props.isFreeBuyBox
);

const sqtvProviderShortName = computed(() => {
	if (country.value === 'AU' && !props.offers.some(offer => offer.package.shortName === 'dnp')) return 'dnp';
	return null;
});

const isSingleQTVActive = computed(() => {
	// FEWER_PROMOTED_TAGS_EXP
	if (props.forceQualityTv) {
		return false;
	}
	// FEWER_PROMOTED_TAGS_EXP

	if (isProUser.value) return false;

	// temporary AU disney SQTV test for 28.05 only
	if (country.value === 'AU' && !props.offers.some(offer => offer.package.shortName === 'dnp')) return true;

	if (
		country.value === 'US' &&
		props.offers.some(offer => offer.package.shortName === 'hlu') &&
		!props.offers.some(offer => offer.package.shortName === 'amp')
	)
		// Temporary release for US, messy logic to replicate with current admin setup
		return true;

	return catalogueItem.value?.placement === 'sqtv';
});

const catalogueProviderShortName = computed(() => catalogueItem.value?.shortName || '');

const { isSQTVEmpty } = useSingleQTV(() => props.title, catalogueProviderShortName, isSingleQTVActive);

const qualityTvActive = computed(() => {
	// FEWER_PROMOTED_TAGS_EXP
	if (props.forceQualityTv) {
		return true;
	}
	// FEWER_PROMOTED_TAGS_EXP
	return (
		!isProUser.value &&
		(!isMounted.value || !showPromotionRowWhenArbitrating.value) &&
		// either placement is overwritten by yo, or the programmatic decision checks
		((catalogueItem.value?.placement === 'qtv' && qualityTVProviderConfig[catalogueItem.value.packageId]) ||
			(!catalogueItem.value?.placement && isQualityTvCountry.value)) &&
		// Itunes TVOD QTV is not a Free Trial, cannot be shown when filter is Free.
		!hideTVODQTV.value
	);
});

const freeTrialSwiperActive = computed(() => {
	// GEO_SWIPER_EXP
	if (geoSwiperActive.value) return true;
	// GEO_SWIPER_EXP

	if (!freeTrialSwiperRules) return false;
	return Object.keys(freeTrialSwiperRules).includes(country.value);
});

function onClickOut(payload: {
	buyboxOffer: TitleOfferFragment | null;
	offerLocation: OfferLocation.FREE_TRIAL;
	row: HTMLElement | null;
}) {
	emit('clickOut', {
		buyboxOffer: payload.buyboxOffer,
		offerLocation: payload.offerLocation,
		row: payload.row,
	});
}

return { __sfc: true,PromotionRowNew, BuyBoxQualityTVNew, SingleQTV, props, isProUser, isArbitrating, country, isMounted, emit, promotionOffer, catalogueItem, inPromotionCatalogue, showPromotionRowWhenArbitrating, geoSwiperExpVariant, geoSwiperLocation, geoSwiperActive, promotionPackageId, isQualityTvCountry, freeTrialActive, hideTVODQTV, sqtvProviderShortName, isSingleQTVActive, catalogueProviderShortName, isSQTVEmpty, qualityTvActive, freeTrialSwiperActive, onClickOut, BuyBoxFreeTrialSwiperOS }
}

})